<template>
  <div :class="['group-section', { ['is-open']: isOpen }]">
    <slot name="groupHeader"></slot>
    <div class="group-search">
      <input
          data-cy="search-filter-input"
          v-model="searchText"
          :id="group.id"
          type="text"
          placeholder="search"
          @input.prevent="delaySearchGroup"/>
      <small v-if="searchText" @click="clearSearch" data-cy="clear-search-filter-button">clear search</small>
      <small v-if="searchError" class="error-label">{{searchError}}</small>
    </div>

    <loading-icon v-if="loading"></loading-icon>

    <div v-if="groupItemsFilteredBySearchText.length && !loading" :class="['group-items-section', { ['has-search']: searchText }]">
      <label
          v-for="item in groupItemsFilteredBySearchText"
          v-if="item.meta_name == null || advancedSegmentEnabled"
          :key="item.tag_id || item.id"
          :class="['tag-item', { disabled: isUpdating }]">
        <input
            v-model="selectedItems"
            :data-name="item.tag || item.name"
            :data-target="group.name"
            :id="item.tag || item.id"
            :value="item.tag && campaignType ? item.tag : item.tag_id || item.id"
            :disabled="isUpdating"
            @change="updateSelectedItems"
            type="checkbox"/>
        <span class="checkmark"></span>
        <span class="label-name">{{labelName(item)}}</span>
        <group-count
            v-if="showCount && itemCounts[item.id]"
            :groupItem="itemCounts[item.id]">
        </group-count>
      </label>

      <sub-config-date-picker
          v-if="showDateRangePicker"
          :isLoadingSub="loadingSub && isUpdating"
          :isUpdating="isUpdating"
          :selectedDateRange="subConfigDate"
          @clearSelectedDates="clearSelectedDates"
          @updateSelectedDate="updateSelectedDate">
      </sub-config-date-picker>
    </div>

    <div class="error-label" v-if="errorMessage">{{errorMessage}}</div>

    <small
        v-if="groupItemsFilteredBySearchText.length < 1 && !loading && !errorMessage"
        class="no-items">
      {{searchText ? 'no search results' : 'group not configured'}}
    </small>

    <template v-if="pagination.current_page">
      <pagination
          v-show="!loading"
          :pagination="pagination"
          showArrows
          @changePage="changePage">
      </pagination>
    </template>
  </div>
</template>

<script>
  import axios from 'axios/index'
  import GroupCount from './GroupCount'
  import { isObject } from '../../utilities'
  import LoadingIcon from '../shared/LoadingIcon'
  import Pagination from '../shared/Pagination'
  import SubConfigDatePicker from './SubConfigDatePicker'
  import { mapGetters } from 'vuex'

  export default {
    props: {
      campaignType: String,
      group: Object,
      isOpen: Boolean,
      isUpdating: Boolean,
      selectedDateRange: String,
      selectedGroups: Array,
      selectedMerchantSegmentIds: Array,
      selectedTags: [Array, Object],
      showCount: Boolean,
      showDateRangePicker: Boolean,
      subConfigDate: String,
      widgetIsOpen: Boolean,
      isGroupsSelected: Boolean
    },

    components: {
      GroupCount,
      LoadingIcon,
      Pagination,
      SubConfigDatePicker
    },

    data() {
      return {
        addConfigDropdown: false,
        chainedCalls: [],
        delayedSearch: null,
        dateRange: null,
        errorMessage: null,
        groupItems: [],
        groupLoaded: null,
        hasPagination: false,
        isTag: false,
        itemCounts: {},
        loading: false,
        loadingSub: false,
        page: 1,
        pagination: {},
        searchError: null,
        searchText: null,
        selectedItems: [],
        validSendTime: true,
      };
    },

    methods: {
      addRemoveTag(clickedItem, isChecked) {
        let updatedTags = this.group.tags || [],
          groupTags = {}
        const clickedTag = clickedItem.value
        if (isChecked) {
          updatedTags = updatedTags.concat(clickedTag)
        } else {
          updatedTags = updatedTags.filter(function (tag) { return tag !== clickedTag })
        }
        groupTags[`${this.group.id}_list`] = updatedTags.join(",")
        this.$emit('updateGroups', groupTags)
        this.$emit('setSelected', this.group.id, this.selectedItems)
      },

      chainGroupCount() {
        const view = this
        this.groupItems.forEach(function(item) {
          let countObject = item.tag ? { loading: true, tag: item.tag } : { loading: true }
          if (!view.itemCounts[item.id]) {
            view.$set(view.itemCounts, item.id, countObject)
            view.chainedCalls.push(view.loadCounts(item.id))
          }
        })
        axios.all(this.chainedCalls)
      },

      changePage(page) {
        this.page = page
        this.loadGroup()
      },

      clearSearch(event) {
        event.stopPropagation()
        this.searchText = null
        this.loadGroup()
      },

      clearSelectedDates() {
        this.dateRange = null
        this.$emit('setSelectedDateRange', null)
        this.$emit('updateGroups', { merchant_segment_ids: this.segmentIdsNoDuplicates, date_range: null })
      },

      collectGroupData(groupData) {
        if (groupData.pagination) {
          if (groupData.pagination.total_pages > 1) {
            this.hasPagination = true
            this.pagination = groupData.pagination
          } else {
            this.hasPagination = false
            this.pagination = {}
          }
        }
        if(!this.groupLoaded) { this.groupLoaded = true }
        this.groupItems = groupData.taggings_count ? groupData.taggings_count : groupData.merchant_segments
        if(this.campaignType === 'Campaign') { this.chainGroupCount() }
        this.errorMessage = null
        this.loading = false
      },

      configSelectedSegmentsToSend(idsToUpdate, updatedDate) {
        const ids = idsToUpdate ? idsToUpdate : this.segmentIdsNoDuplicates

        if (updatedDate) { this.dateRange = updatedDate }

        let segmentsToSend = { merchant_segment_ids: ids },
          selectedDateRange = updatedDate || this.selectedDateRange || this.dateRange,
          shouldUpdateDateRange = this.selectedDateRange !== selectedDateRange

        if (this.showDateRangePicker && shouldUpdateDateRange) { this.$emit('setSelectedDateRange', selectedDateRange) }

        segmentsToSend.date_range = selectedDateRange
        return segmentsToSend
      },

      delaySearchGroup(event) {
        event.stopPropagation()
        const view = this
        this.searchText = this.searchText.replace(/<[^>]*>/g,'')
        clearTimeout(this.delayedSearch)
        this.delayedSearch = setTimeout(function() {
          view.page = 1
          view.loadGroup()
        }, 500)
      },

      groupUrl() {
        const baseUrl = `/api/v3/groups/`
        let url = ''
        if (this.group.name === 'misc') {
          url = `${baseUrl}merchant_segments?misc=true`
        } else {
          const taggingUrl = `merchant_taggings?context=${this.groupName}`,
            merchantSegmentsUrl = `merchant_segments?segment_id=${this.group.id}`,
            configuredUrl = this.isTag ? taggingUrl : merchantSegmentsUrl
          url = `${baseUrl}${configuredUrl}&page=${this.page}`
        }

        if (this.searchText) {
          this.searchText = this.searchText.trim()
          url = `${url}&search=${this.searchText}`
        }

        return url
      },

      labelName(item){
        const name = item.title ? item.title : item.name
        return name.replace(/\_/g, ' ')
      },

      loadCounts(id) {
        const baseUrl = '/api/v3/groups/',
          tag = this.itemCounts[id].tag,
          segmentUrl = `segment_count?segment_id=${id}`,
          tagUrl = `tag_count?tag=${tag}&context=${this.groupName}`,
          url = tag ? baseUrl + tagUrl : baseUrl + segmentUrl
        axios.get(url)
          .then((response) => {
            const data = response.data
            if (isObject(data)) {
              this.$set(this.itemCounts, id, { email: data.email, sms: data.sms })
            } else {
              this.$set(this.itemCounts, id, { error: 'count not loaded' })
            }
          })
          .catch((error) => {
            this.$set(this.itemCounts, id, { error: `issue loading count. ${error}` })
          })
      },

      loadGroup() {
        if (this.searchText && this.searchText.length > 150) {
          this.searchError = 'search text cannot be more than 150 characters'
        } else {
          this.loading = true
          this.searchError = null
          const url = this.groupUrl()
          axios.get(url)
            .then((response) => {
              const data = response.data,
                hasItem = data.taggings_count || data.merchant_segments
              if (hasItem) {
                this.$emit('updatePrimaryLocation', data.primary_location_context)
                this.collectGroupData(data)
              } else {
                this.errorMessage = 'no group loaded'
                this.loading = false
              }
            })
            .catch((error) => {
              this.loading = false
              this.errorMessage = `issue loading group. ${error}`
            })
        }
      },

      removeAllIdsFromGroup() {
        const groupIds = this.groupItems.map(item => item.id),
          idsToUpdate = this.selectedMerchantSegmentIds.filter(id => !groupIds.includes(id))
        this.$emit('updateGroups', { merchant_segment_ids: idsToUpdate })
      },

      setSelected() {
        if (this.isTag) {
          const tags = this.group.tags
          return tags ? tags : (tags && this.selectedItems) ? this.tagsNoDuplicates : []
        } else {
          return this.selectedMerchantSegmentIds ? this.selectedMerchantSegmentIds : []
        }
      },

      updateSelectedDate(dateStr) {
        this.loadingSub = true
        const dataToSend = this.configSelectedSegmentsToSend(null, dateStr)
        this.$emit('updateGroups', dataToSend)
      },

      updateSelectedIds(clickedItem, isChecked) {
        let idsToUpdate = [],
          dataToSend = {}
        const clickedItemId = parseInt(clickedItem.id),
          allIds = this.segmentIdsNoDuplicates
        if (isChecked) {
          idsToUpdate = allIds
        } else {
          idsToUpdate = allIds.filter(function (id) { return id !== clickedItemId; })
          if (idsToUpdate.length < 1 && this.campaignType) { idsToUpdate = [""] }
        }
        dataToSend = this.configSelectedSegmentsToSend(idsToUpdate)

        this.$emit('updateGroups', dataToSend)
        this.$emit('setSelected', this.group.id, idsToUpdate)
        if (this.showDateRangePicker) this.loadGroup()
      },

      updateSelectedItems(event) {
        event.stopPropagation()
        const clickedItem = event.target,
          isChecked = clickedItem.checked,
          target = event.target.dataset && event.target.dataset.target

        if (this.isTag) {
          this.addRemoveTag(clickedItem, isChecked)
        } else {
          this.updateSelectedIds(clickedItem, isChecked)
        }
      },
    },

    created() {
      if (this.group.context) { this.isTag = true }
      this.selectedItems = this.campaignType ? this.setSelected() : this.selectedItems
    },

    beforeDestroy() {
      // removing carrier from list if this group is filtered out from the groups filter list
      // this happens because backup campaigns filters out carrier group when enabled
      if (this.group.name === 'carrier' && this.selectedItems.length > 0) { this.removeAllIdsFromGroup() }
    },

    computed: {
      groupItemsFilteredBySearchText() {
        if(this.searchText){
          return this.groupItems.filter(group => {
            const groupLabel = group.title ? group.title : group.name
            return groupLabel.toLowerCase().includes(this.searchText.toLowerCase())
          })
        }else{
          return this.groupItems
        }
      },

      groupName: function() {
        return this.group.context
      },

      segmentIdsNoDuplicates: function() {
        const segments = this.selectedMerchantSegmentIds ? this.selectedMerchantSegmentIds : []
        return this.campaignType ? [...new Set([...segments ,...this.selectedItems])] : this.selectedItems
      },

      tagsNoDuplicates: function() {
        const tags = this.group.tags ? this.group.tags : []
        return this.campaignType ? [...new Set([...tags, ...this.selectedItems])] : this.selectedItems
      },

      ...mapGetters(['advancedSegmentEnabled']),
    },

    watch: {
      group: function() {
        this.selectedItems = this.campaignType ? this.setSelected() : this.selectedItems
      },

      isOpen: function() {
        if(!this.groupLoaded) {
          this.loadGroup()
        }
      },

      isUpdating: function() {
        if (this.loadingSub && !this.isUpdating) { this.loadingSub = false }
      },

      widgetIsOpen: function() {
        if(this.groupLoaded) {
          this.page = 1
          this.loadGroup()
        }
      },
    },
  }
</script>
